import { computed, inject, Injectable, resource, signal } from '@angular/core';
import { StockPostingRepository } from '../repositories/stock-posting.repository';
import {
  StockPosting,
  StockPostingCreate,
  StockPostingFinalize,
  StockPostingPatch,
} from '../types/stock-posting';

/** Header state of the user's open stock posting document (one per user). */
@Injectable()
export class StockPostingService {
  private readonly repository = inject(StockPostingRepository);

  readonly #stockPosting = signal<StockPosting | null>(null);

  readonly stockPosting = this.#stockPosting.asReadonly();
  readonly mandatoryFields = computed(() => this.stockPosting()?.mandatoryFields ?? null);

  protected readonly signatureResource = resource({
    params: () => this.stockPosting()?.id,
    loader: ({ params: id }) => {
      return this.repository.loadSignature(id);
    },
    defaultValue: null,
  });

  readonly signature = computed(() => {
    return this.signatureResource.hasValue()
      ? (this.signatureResource.value() ?? null)
      : null;
  });

  /** Loads the open document; a failing request (e.g. 404) counts as "none open". */
  async load(): Promise<StockPosting | null> {
    try {
      const result = await this.repository.loadOpen();
      this.#stockPosting.set(result?.id ? result : null);
    } catch {
      this.#stockPosting.set(null);
    }
    return this.stockPosting();
  }

  async create(body: StockPostingCreate): Promise<StockPosting> {
    const result = await this.repository.create(body);
    this.#stockPosting.set(result);
    return result;
  }

  async patchHeader(body: Partial<StockPostingPatch>): Promise<StockPosting> {
    const result = await this.repository.patch(this.requireId(), body);
    this.#stockPosting.set(result);
    return result;
  }

  async finalize(body: StockPostingFinalize): Promise<void> {
    await this.repository.finalize(this.requireId(), body);
    this.#stockPosting.set(null);
  }

  async delete(): Promise<void> {
    await this.repository.delete(this.requireId());
    this.#stockPosting.set(null);
  }

  async sign(
    id: string | number,
    signature: Blob,
    signerName?: string | null
  ): Promise<boolean> {
    await this.repository.signDocument(id, signature, signerName);
    return true;
  }

  async loadSignature(id: string): Promise<Blob | null> {
    return await this.repository.loadSignature(id);
  }

  private requireId(): number {
    const id = this.stockPosting()?.id;
    if (id === undefined) throw new Error('No open stock posting');
    return id;
  }
}
